<template>
  <v-container fluid class="er-wrap">
    <!-- Header card: page identity on top, the campaign switch that re-scopes
         every tab below it as a divided second row. -->
    <div class="er-head">
      <div class="er-sub-header">
        <div class="er-sub-header__left">
          <button
            type="button"
            class="er-head__back"
            title="Back"
            @click="goBack()"
          >
            <v-icon size="18" color="var(--app-text-muted)">mdi-arrow-left</v-icon>
          </button>
          <div>
            <h2 class="er-sub-header__title">Email Reminder</h2>
            <p class="er-sub-header__subtitle">{{ subtitle }}</p>
          </div>
        </div>
      </div>

      <!-- Reminder type (campaign) dropdown - re-scopes every tab below. -->
      <div class="er-campaign-bar">
        <er-select
          v-model="campaignLabel"
          :options="campaignOptions"
          width="240px"
        />
        <span class="er-campaign-bar__desc">{{ activeCampaign.desc }}</span>
      </div>
    </div>

    <!-- Segmented tab bar -->
    <div class="er-tabbar">
      <button
        v-for="(t, i) in tabs"
        :key="i"
        class="er-tabbar__tab"
        :class="{ 'er-tabbar__tab--active': tab === i }"
        @click="selectTab(i)"
      >
        <v-icon size="15" :color="tab === i ? '#0ea5e9' : '#94a3b8'">
          {{ t.icon }}
        </v-icon>
        {{ t.label }}
      </button>
    </div>

    <!-- ============================ TEMPLATES ============================ -->
    <div v-show="tab === 0" class="er-panel">
      <div v-if="loading.templates" class="er-loading">
        <v-progress-circular indeterminate color="var(--app-accent)" size="28" />
        <span class="er-loading__text">Loading templates…</span>
      </div>

      <template v-else>
        <!-- Template picker - pick one of the three to edit -->
        <div class="er-tplpicker">
          <button
            v-for="(tpl, i) in templates"
            :key="tpl.uuid"
            class="er-tplpick"
            :class="{ 'er-tplpick--active': templateIndex === i }"
            @click="templateIndex = i"
          >
            <span class="er-tplpick__icon">
              <v-icon
                size="19"
                :color="templateIndex === i ? '#0ea5e9' : '#94a3b8'"
              >
                {{
                  tpl.templateKey === "final"
                    ? "mdi-flag-checkered"
                    : "mdi-file-document-outline"
                }}
              </v-icon>
            </span>
            <span class="er-tplpick__text">
              <span class="er-tplpick__name">{{ tpl.name }}</span>
              <span
                class="er-tplpick__status"
                :class="
                  isTemplateSet(tpl)
                    ? 'er-tplpick__status--set'
                    : 'er-tplpick__status--empty'
                "
              >
                <span class="er-tplpick__dot"></span>
                {{ isTemplateSet(tpl) ? "Configured" : "Not set" }}
              </span>
            </span>
          </button>
        </div>

        <!-- Editor for the selected template -->
        <div v-if="activeTemplate" :key="activeTemplate.uuid" class="er-card">
          <div class="er-card__head">
            <div class="er-card__head-left">
              <div class="er-card__icon">
                <v-icon size="20" color="var(--app-accent)">
                  {{
                    activeTemplate.templateKey === "final"
                      ? "mdi-flag-checkered"
                      : "mdi-file-document-outline"
                  }}
                </v-icon>
              </div>
              <h3 class="er-card__title">{{ activeTemplate.name }}</h3>
            </div>
            <span class="er-chip">{{ activeTemplate.templateKey }}</span>
          </div>

          <!-- claim_followup has an admin-editable subject (other campaigns
               reuse the confirmation email's subject). -->
          <template v-if="isClaimFollowup">
            <label class="er-label">Email Subject</label>
            <v-text-field
              v-model="activeTemplate.emailSubject"
              dense
              outlined
              hide-details
              class="er-input"
              placeholder="Email subject"
            ></v-text-field>

            <!-- Recipients live in this card too - saved together with the
                 template via the single Save button at the bottom. -->
            <label class="er-label er-label--section">Email Recipient</label>
            <p class="er-text">
              The reminder is sent to these addresses. Type an email and press
              Enter (or comma) - each address shows as its own tag.
            </p>
            <label class="er-label">To</label>
            <v-combobox
              v-model="setting.toRecipients"
              multiple
              chips
              small-chips
              deletable-chips
              hide-no-data
              hide-selected
              append-icon=""
              :delimiters="[',', ';']"
              dense
              outlined
              hide-details
              class="er-input er-recipients"
              placeholder="support@hrdcorp.gov.my"
            ></v-combobox>
            <label class="er-label">Cc</label>
            <v-combobox
              v-model="setting.ccRecipients"
              multiple
              chips
              small-chips
              deletable-chips
              hide-no-data
              hide-selected
              append-icon=""
              :delimiters="[',', ';']"
              dense
              outlined
              hide-details
              class="er-input er-recipients"
              placeholder="name@hrdcorp.gov.my"
            ></v-combobox>
          </template>

          <label class="er-label">Email Body</label>

          <!-- Attachment preview rendered atop the email body. For the Physical
               Event template the chips are fixed at JD14 + T3 (2); the Online
               Event template additionally carries the Attendance Form (3). The
               Final Reminder template is shared by both online and physical
               events, so it shows an Online/Physical toggle to preview either
               set. Illustrative cue only - the backend composes the real email.
               Hidden for claim_followup (digest email has no attachments). -->
          <div v-if="!isClaimFollowup" class="er-mailatch">
            <div class="er-mailatch__bar">
              <v-icon size="15" color="var(--app-text-muted)">mdi-paperclip</v-icon>
              <span class="er-mailatch__label">
                {{ previewAttachments.length }}
                {{
                  previewAttachments.length === 1 ? "Attachment" : "Attachments"
                }}
              </span>
              <!-- Toggle only for the shared Final Reminder template; the
                   dynamic Online/Physical templates have a fixed set. -->
              <div v-if="isFinalTemplate" class="er-atch-seg er-atch-seg--sm">
                <button
                  type="button"
                  class="er-atch-seg__btn"
                  :class="{ 'er-atch-seg__btn--active': attachmentPreviewOnline }"
                  @click="attachmentPreviewOnline = true"
                >
                  Online
                </button>
                <button
                  type="button"
                  class="er-atch-seg__btn"
                  :class="{ 'er-atch-seg__btn--active': !attachmentPreviewOnline }"
                  @click="attachmentPreviewOnline = false"
                >
                  Physical
                </button>
              </div>
            </div>
            <div class="er-mailatch__files">
              <div
                v-for="a in previewAttachments"
                :key="a.stageId"
                class="er-mailchip"
                :title="a.label"
              >
                <v-icon size="17" color="#ef4444">mdi-file-pdf-box</v-icon>
                <span class="er-mailchip__name">{{ a.file }}</span>
              </div>
            </div>
          </div>

          <div class="er-editor">
            <tiptap-vuetify
              v-model="activeTemplate.emailContent"
              :extensions="extensions"
            />
          </div>

          <div class="er-card__actions">
            <button
              class="er-btn er-btn--ghost"
              @click="previewTemplate(activeTemplate)"
            >
              <v-icon size="15" class="mr-1">mdi-eye-outline</v-icon> Preview
            </button>
            <button
              class="er-btn er-btn--primary"
              :disabled="activeTemplate._saving"
              @click="saveTemplate(activeTemplate)"
            >
              <v-progress-circular
                v-if="activeTemplate._saving"
                indeterminate
                size="14"
                width="2"
                color="#fff"
                class="mr-1"
              />
              {{
                activeTemplate._saving
                  ? "Saving…"
                  : isClaimFollowup
                  ? "Save"
                  : "Save Template"
              }}
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- ============================ FREQUENCIES ============================ -->
    <div v-show="tab === 1" class="er-panel">
      <!-- form_revert: editable "after the training end date" cadence. -->
      <div v-if="isFormRevert" class="er-card">
        <div class="er-card__head">
          <div class="er-card__head-left">
            <div class="er-card__icon">
              <v-icon size="20" color="var(--app-accent)">mdi-calendar-clock</v-icon>
            </div>
            <h3 class="er-card__title">Dynamic Reminder Frequencies</h3>
          </div>
        </div>

        <div class="er-cadence__summary">
          <v-icon size="15" color="var(--app-accent)" class="mr-1">
            mdi-information-outline
          </v-icon>
          <span>{{ cadenceDescription }}</span>
        </div>

        <div class="er-cadence">
          <div class="er-cadence__field">
            <label class="er-label">First reminder - days after end date</label>
            <v-text-field
              v-model.number="setting.afterOffsetDays"
              type="number"
              min="0"
              dense
              outlined
              hide-details
              class="er-input"
              style="max-width: 130px"
            ></v-text-field>
          </div>
          <div class="er-cadence__field">
            <label class="er-label">Repeat every (days)</label>
            <v-text-field
              v-model.number="setting.afterIntervalDays"
              type="number"
              min="1"
              dense
              outlined
              hide-details
              class="er-input"
              style="max-width: 130px"
            ></v-text-field>
          </div>
          <div class="er-cadence__field">
            <label class="er-label">Stop after (months)</label>
            <v-text-field
              v-model.number="setting.afterHorizonMonths"
              type="number"
              min="1"
              dense
              outlined
              hide-details
              class="er-input"
              style="max-width: 130px"
            ></v-text-field>
          </div>
        </div>

        <div class="er-card__actions er-card__actions--start">
          <button
            class="er-btn er-btn--primary"
            :disabled="loading.settingSave"
            @click="saveSetting"
          >
            <v-progress-circular
              v-if="loading.settingSave"
              indeterminate
              size="14"
              width="2"
              color="#fff"
              class="mr-1"
            />
            Save
          </button>
        </div>
      </div>

      <!-- claim_followup: run cadence + late threshold. -->
      <div v-if="isClaimFollowup" class="er-card">
        <div class="er-card__head">
          <div class="er-card__head-left">
            <div class="er-card__icon">
              <v-icon size="20" color="var(--app-accent)">mdi-calendar-clock</v-icon>
            </div>
            <h3 class="er-card__title">Dynamic Reminder Frequencies</h3>
          </div>
        </div>
        <div class="er-cadence__summary">
          <v-icon size="15" color="var(--app-accent)" class="mr-1">
            mdi-information-outline
          </v-icon>
          <span>{{ claimFollowupDescription }}</span>
        </div>
        <div class="er-cadence">
          <div class="er-cadence__field">
            <label class="er-label">Run every (days)</label>
            <v-text-field
              v-model.number="setting.afterIntervalDays"
              type="number"
              min="1"
              dense
              outlined
              hide-details
              class="er-input"
              style="max-width: 130px"
            ></v-text-field>
          </div>
          <div class="er-cadence__field">
            <label class="er-label">Late after (days pending)</label>
            <v-text-field
              v-model.number="setting.lateThresholdDays"
              type="number"
              min="0"
              dense
              outlined
              hide-details
              class="er-input"
              style="max-width: 130px"
            ></v-text-field>
          </div>
        </div>
        <div class="er-card__actions er-card__actions--start">
          <button
            class="er-btn er-btn--primary"
            :disabled="loading.settingSave"
            @click="saveSetting"
          >
            <v-progress-circular
              v-if="loading.settingSave"
              indeterminate
              size="14"
              width="2"
              color="#fff"
              class="mr-1"
            />
            Save
          </button>
        </div>
      </div>

      <div v-if="isGrant" class="er-card">
        <div class="er-card__head">
          <div class="er-card__head-left">
            <div class="er-card__icon">
              <v-icon size="20" color="var(--app-accent)">mdi-calendar-clock</v-icon>
            </div>
            <h3 class="er-card__title">Dynamic Reminder Frequencies</h3>
          </div>
        </div>

        <div v-if="loading.frequencies" class="er-loading">
          <v-progress-circular indeterminate color="var(--app-accent)" size="28" />
        </div>

        <table v-else class="er-table er-table--form">
          <thead>
            <tr>
              <th style="width: 150px">Days Before</th>
              <th>Label</th>
              <th style="width: 130px">Status</th>
              <th style="width: 160px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(f, i) in frequencies" :key="f.uuid || 'new-' + i">
              <td data-label="Days Before">
                <v-text-field
                  v-model.number="f.daysBefore"
                  type="number"
                  min="1"
                  dense
                  outlined
                  hide-details
                  class="er-input"
                ></v-text-field>
              </td>
              <td data-label="Label">
                <v-text-field
                  v-model="f.label"
                  dense
                  outlined
                  hide-details
                  placeholder="e.g. First Reminder"
                  class="er-input"
                ></v-text-field>
              </td>
              <td data-label="Status">
                <v-checkbox
                  v-model="f.isActive"
                  hide-details
                  dense
                  class="er-checkbox"
                >
                  <template v-slot:label><span>Active</span></template>
                </v-checkbox>
              </td>
              <td class="er-table__actions">
                <div class="er-row-actions">
                  <button
                    class="er-action-btn"
                    :disabled="f._saving"
                    @click="saveFrequency(f, i)"
                  >
                    <v-icon size="13">mdi-content-save-outline</v-icon>
                    {{ f._saving ? "…" : "Save" }}
                  </button>
                  <button
                    class="er-action-btn er-action-btn--danger"
                    @click="removeFrequency(f, i)"
                  >
                    <v-icon size="13">mdi-trash-can-outline</v-icon> Remove
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <button class="er-btn er-btn--ghost mt-3" @click="addFrequencyRow">
          <v-icon size="16" class="mr-1">mdi-plus</v-icon> Add Frequency
        </button>
      </div>

      <div v-if="isGrant" class="er-card">
        <div class="er-card__head">
          <div class="er-card__head-left">
            <div class="er-card__icon er-card__icon--amber">
              <v-icon size="20" color="#f59e0b">mdi-flag-checkered</v-icon>
            </div>
            <h3 class="er-card__title">Final Reminder</h3>
          </div>
        </div>
        <p class="er-text">
          A single final reminder is sent this many days before the event date.
          The last day a grant can be applied is 1 day before the event.
        </p>
        <label class="er-label">Days before event date</label>
        <div class="er-inline-row">
          <v-text-field
            v-model.number="setting.finalReminderDaysBefore"
            type="number"
            min="0"
            dense
            outlined
            hide-details
            class="er-input"
            style="max-width: 130px"
          ></v-text-field>
          <button
            class="er-btn er-btn--primary"
            :disabled="loading.settingSave"
            @click="saveSetting"
          >
            <v-progress-circular
              v-if="loading.settingSave"
              indeterminate
              size="14"
              width="2"
              color="#fff"
              class="mr-1"
            />
            Save
          </button>
        </div>
      </div>
    </div>

    <!-- ============================ SCHEDULE ============================ -->
    <div v-show="tab === 2" class="er-panel">
      <div class="er-card">
        <div class="er-card__head">
          <div class="er-card__head-left">
            <div class="er-card__icon">
              <v-icon size="20" color="var(--app-accent)">mdi-clock-outline</v-icon>
            </div>
            <h3 class="er-card__title">Cron Schedule</h3>
          </div>
          <span
            class="er-status"
            :class="setting.cronEnabled ? 'er-status--on' : 'er-status--off'"
          >
            <span class="er-status__dot"></span>
            {{ setting.cronEnabled ? "Enabled" : "Disabled" }}
          </span>
        </div>

        <p class="er-text">
          When enabled, reminders are blasted automatically once a day at the
          time below. When disabled, nothing is sent automatically - use “Run
          Reminders Now” to send manually.
        </p>

        <div class="er-toggle-row">
          <v-switch
            v-model="setting.cronEnabled"
            color="#10b981"
            inset
            hide-details
            class="mt-0 pt-0 er-switch"
          ></v-switch>
          <span class="er-toggle-label">
            {{
              setting.cronEnabled
                ? "Automatic blasting ON"
                : "Automatic blasting OFF"
            }}
          </span>
        </div>

        <div v-if="setting.cronEnabled" class="er-grid">
          <div>
            <label class="er-label">Blast Time (24-hour)</label>
            <div class="er-timefield">
              <span class="er-field-icon">
                <v-icon size="16" color="var(--app-accent)">mdi-clock-outline</v-icon>
              </span>
              <er-select
                v-model="blastHour"
                :options="hourOptions"
                width="78px"
                align="center"
              />
              <span class="er-timefield__sep">:</span>
              <er-select
                v-model="blastMinute"
                :options="minuteOptions"
                width="78px"
                align="center"
              />
            </div>
          </div>
          <div>
            <label class="er-label">Timezone</label>
            <div class="er-timefield">
              <span class="er-field-icon">
                <v-icon size="16" color="var(--app-accent)">mdi-earth</v-icon>
              </span>
              <er-select
                v-model="setting.timezone"
                :options="timezoneList"
                width="248px"
                searchable
                search-placeholder="Search timezone…"
              />
            </div>
          </div>
        </div>

        <div class="er-card__actions er-card__actions--start">
          <button
            class="er-btn er-btn--primary"
            :disabled="loading.settingSave"
            @click="saveSetting"
          >
            <v-progress-circular
              v-if="loading.settingSave"
              indeterminate
              size="14"
              width="2"
              color="#fff"
              class="mr-1"
            />
            Save Schedule
          </button>
          <v-tooltip top max-width="280">
            <template v-slot:activator="{ on, attrs }">
              <button
                class="er-btn er-btn--outline"
                :disabled="loading.runNow"
                @click="runNow"
                v-bind="attrs"
                v-on="on"
              >
                <v-progress-circular
                  v-if="loading.runNow"
                  indeterminate
                  size="14"
                  width="2"
                  color="var(--app-accent)"
                  class="mr-1"
                />
                <v-icon v-else size="15" class="mr-1">
                  mdi-send-clock-outline
                </v-icon>
                Run Reminders Now
              </button>
            </template>
            <span>
              Sends only the reminders due today - the same batch the daily
              cron would send. Reminders scheduled for future days are not
              included.
            </span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <!-- ============================ HISTORY ============================ -->
    <div v-show="tab === 3" class="er-panel">
      <!-- Upcoming -->
      <div class="er-card">
        <div class="er-card__head">
          <div class="er-card__head-left">
            <div class="er-card__icon er-card__icon--green">
              <v-icon size="20" color="#10b981">mdi-calendar-arrow-right</v-icon>
            </div>
            <h3 class="er-card__title">Upcoming - Next 7 Days</h3>
          </div>
          <button class="er-action-btn" @click="loadUpcoming">
            <v-icon size="13">mdi-refresh</v-icon> Refresh
          </button>
        </div>

        <div v-if="loading.upcoming" class="er-loading">
          <v-progress-circular indeterminate color="var(--app-accent)" size="28" />
        </div>
        <template v-else>
          <!-- Day sub-tabs (next 7 days) -->
          <div class="er-daytabs">
            <div v-for="(d, i) in upcomingDays" :key="d" class="er-daytab-wrap">
              <button
                class="er-daytab"
                :class="{ 'er-daytab--active': upcomingDayIndex === i }"
                @click="selectDay(i)"
              >
                <span class="er-daytab__label">{{ upcomingDayLabel(d) }}</span>
                <span class="er-daytab__date">{{ formatDayShort(d) }}</span>
                <span
                  class="er-daytab__count"
                  :class="{ 'er-daytab__count--zero': !upcomingCountByDay[d] }"
                >
                  {{ upcomingCountByDay[d] || 0 }}
                </span>
              </button>
              <button
                class="er-daytab__preview"
                :title="`Preview the email(s) for ${upcomingDayLabel(d)}`"
                @click="previewUpcomingDay(d)"
              >
                <v-icon size="11">mdi-eye-outline</v-icon> Preview
              </button>
            </div>
          </div>

          <!-- Selected day - grouped by category -->
          <div v-if="!upcomingGroups.length" class="er-empty">
            <v-icon size="34" color="#cbd5e1">mdi-calendar-blank-outline</v-icon>
            <span>
              No reminders scheduled for
              {{ upcomingDayLabel(upcomingSelectedDate) }}.
            </span>
          </div>
          <div v-else>
            <div v-for="grp in upcomingGroups" :key="grp.category" class="er-cgroup">
              <div class="er-cgroup__head">
                <span class="er-cat" :class="catClass(grp.reminderType)">
                  {{ grp.category }}
                </span>
                <span class="er-cgroup__title">{{ groupTitle(grp) }}</span>
                <span class="er-cgroup__count">{{ grp.items.length }}</span>
              </div>
              <div class="er-table-scroll">
                <table class="er-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Event</th>
                      <th style="width: 130px">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(it, idx) in grp.items" :key="idx">
                      <td class="er-td-strong">{{ it.companyName || "-" }}</td>
                      <td>{{ it.eventTitle || "-" }}</td>
                      <td>
                        <span class="er-tag">
                          {{ it.typeLabel || (it.isOnline ? "Online" : "Physical") }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Sent log -->
      <div class="er-card">
        <div class="er-card__head">
          <div class="er-card__head-left">
            <div class="er-card__icon">
              <v-icon size="20" color="var(--app-accent)">mdi-history</v-icon>
            </div>
            <h3 class="er-card__title">Sent History</h3>
          </div>
          <button class="er-action-btn" @click="loadLogs">
            <v-icon size="13">mdi-refresh</v-icon> Refresh
          </button>
        </div>

        <div v-if="loading.logs" class="er-loading">
          <v-progress-circular indeterminate color="var(--app-accent)" size="28" />
        </div>
        <div v-else-if="!logs.length" class="er-empty">
          <v-icon size="34" color="#cbd5e1">mdi-email-outline</v-icon>
          <span>No reminder emails sent yet.</span>
        </div>
        <div v-else class="er-table-scroll">
          <table class="er-table">
            <thead>
              <tr>
                <th style="width: 140px">Sent At</th>
                <th>Client</th>
                <th>Event</th>
                <th style="width: 96px">Category</th>
                <th>Subject</th>
                <th>To</th>
                <th>CC</th>
                <th style="width: 84px">Status</th>
                <th style="width: 70px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.uuid">
                <td>{{ formatDateTime(log.sentAt || log.timestampCreated) }}</td>
                <td class="er-td-strong">{{ log.companyName || "-" }}</td>
                <td>{{ log.eventTitle || "-" }}</td>
                <td>
                  <span class="er-cat" :class="catClass(log.reminderType)">
                    {{ log.category }}
                  </span>
                </td>
                <td>{{ log.emailSubject || "-" }}</td>
                <td class="er-td-muted">{{ joinRecipients(log.toRecipients) }}</td>
                <td class="er-td-muted">{{ joinRecipients(log.ccRecipients) }}</td>
                <td>
                  <span
                    class="er-status er-status--sm"
                    :class="log.status === 1 ? 'er-status--on' : 'er-status--fail'"
                  >
                    <span class="er-status__dot"></span>
                    {{ log.status === 1 ? "Sent" : "Failed" }}
                  </span>
                </td>
                <td>
                  <button class="er-action-btn er-action-btn--xs" @click="viewLog(log)">
                    <v-icon size="13">mdi-eye-outline</v-icon> View
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="logs.length" class="er-pagination">
          <span class="er-pagination__total">{{ logCount }} total</span>
          <div class="er-pagination__nav" v-if="logCount > logLimit">
            <button
              class="er-action-btn"
              :disabled="logPage <= 1"
              @click="changeLogPage(-1)"
            >
              <v-icon size="13">mdi-chevron-left</v-icon> Prev
            </button>
            <span class="er-pagination__label">
              Page {{ logPage }} / {{ logTotalPages }}
            </span>
            <button
              class="er-action-btn"
              :disabled="logPage >= logTotalPages"
              @click="changeLogPage(1)"
            >
              Next <v-icon size="13">mdi-chevron-right</v-icon>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview dialog -->
    <v-dialog v-model="previewDialog" max-width="720">
      <div class="er-modal">
        <div class="er-modal__head">
          <h3 class="er-modal__title">{{ previewTitle }}</h3>
          <button class="er-back-btn" @click="previewDialog = false">
            <v-icon size="16" color="var(--app-text-muted)">mdi-close</v-icon>
          </button>
        </div>
        <div class="er-modal__body" v-html="previewHtml"></div>
      </div>
    </v-dialog>

    <!-- Email preview — the full email(s), shared by Templates + Insights -->
    <v-dialog v-model="emailPreview.open" max-width="820">
      <div class="er-modal">
        <div class="er-modal__head">
          <h3 class="er-modal__title">{{ emailPreview.title }}</h3>
          <button class="er-back-btn" @click="emailPreview.open = false">
            <v-icon size="16" color="var(--app-text-muted)">mdi-close</v-icon>
          </button>
        </div>
        <div class="er-modal__body">
          <div v-if="emailPreview.loading" class="er-loading">
            <v-progress-circular indeterminate color="var(--app-accent)" size="28" />
          </div>
          <div v-else-if="!emailPreview.emails.length" class="er-empty">
            <v-icon size="34" color="#cbd5e1">mdi-email-off-outline</v-icon>
            <span>No emails would be sent on this day.</span>
          </div>
          <template v-else>
            <p v-if="emailPreview.hint" class="er-preview-hint">
              <v-icon size="14" color="var(--app-accent)">mdi-information-outline</v-icon>
              {{ emailPreview.hint }}
            </p>
            <div
              v-for="(em, i) in emailPreview.emails"
              :key="i"
              class="er-preview-email"
            >
              <div
                v-if="em.subject || em.to || (em.cc && em.cc.length)"
                class="er-preview-meta"
              >
                <div v-if="em.subject" class="er-preview-row">
                  <span class="er-preview-k">Subject</span>
                  <span class="er-preview-v">{{ em.subject }}</span>
                </div>
                <div v-if="em.to" class="er-preview-row">
                  <span class="er-preview-k">To</span>
                  <span class="er-preview-v">{{
                    em.to.length ? em.to.join(", ") : "—"
                  }}</span>
                </div>
                <div v-if="em.cc && em.cc.length" class="er-preview-row">
                  <span class="er-preview-k">CC</span>
                  <span class="er-preview-v">{{ em.cc.join(", ") }}</span>
                </div>
              </div>
              <div v-if="em.note" class="er-preview-note">
                <v-icon size="13" color="#b45309">mdi-alert-outline</v-icon>
                {{ em.note }}
              </div>
              <div
                v-if="em.html"
                class="er-preview-html"
                v-html="em.html"
              ></div>
            </div>
          </template>
        </div>
      </div>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import ErSelect from "./ErSelect.vue";
import moment from "moment";
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from "tiptap-vuetify";

export default {
  mixins: [hrdcFunction],
  components: { TiptapVuetify, ErSelect },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    activeTemplate() {
      return this.templates[this.templateIndex] || null;
    },
    isFormRevert() {
      return this.campaign === "form_revert";
    },
    isClaimFollowup() {
      return this.campaign === "claim_followup";
    },
    isGrant() {
      return this.campaign === "grant_application";
    },
    // The campaign meta in focus (drives the dropdown icon + description).
    activeCampaign() {
      return (
        this.campaigns.find((c) => c.key === this.campaign) || this.campaigns[0]
      );
    },
    // Grouped by party ("Clients" now; "HRDC Corp" later) for the dropdown.
    campaignOptions() {
      const groups = [];
      for (const c of this.campaigns) {
        const party = c.party || "Other";
        let g = groups.find((x) => x.group === party);
        if (!g) {
          g = { group: party, items: [] };
          groups.push(g);
        }
        g.items.push(c.label);
      }
      return groups;
    },
    // ErSelect works on the visible label; map it back to the campaign key.
    campaignLabel: {
      get() {
        return this.activeCampaign ? this.activeCampaign.label : "";
      },
      set(label) {
        const c = this.campaigns.find((x) => x.label === label);
        if (c) this.selectCampaign(c.key);
      },
    },
    subtitle() {
      if (this.isClaimFollowup) {
        return "Daily reminder to HRDC Corp listing claims pending approval past the late threshold.";
      }
      return this.isFormRevert
        ? "Reminder emails for grant-approved clients to revert their completed JD14 / T3 / Attendance forms."
        : "Reminder emails for clients who have not yet submitted their Grant Approval No.";
    },
    // Plain-English summary of the form_revert cadence, rebuilt live from the
    // offset / interval / horizon inputs as the admin edits them.
    cadenceDescription() {
      const offset = parseInt(this.setting.afterOffsetDays);
      const interval = parseInt(this.setting.afterIntervalDays);
      const months = parseInt(this.setting.afterHorizonMonths);
      const plural = (n, unit) => `${n} ${unit}${n === 1 ? "" : "s"}`;

      const first =
        !Number.isFinite(offset) || offset <= 0
          ? "A reminder goes out on the training end date"
          : `The first reminder goes out ${plural(
              offset,
              "day"
            )} after the training end date`;

      let repeat = "";
      if (Number.isFinite(interval) && interval > 0) {
        repeat =
          interval === 1
            ? ", then daily"
            : interval === 7
            ? ", then weekly"
            : `, then every ${plural(interval, "day")}`;
      }

      const horizon =
        Number.isFinite(months) && months > 0
          ? `, for up to ${plural(months, "month")}`
          : "";

      return `${first}${repeat}${horizon}`;
    },
    // Plain-English summary of the claim_followup digest, rebuilt live from the
    // run-interval / late-threshold inputs as the admin edits them.
    claimFollowupDescription() {
      const interval = parseInt(this.setting.afterIntervalDays);
      const threshold = parseInt(this.setting.lateThresholdDays);
      const plural = (n, unit) => `${n} ${unit}${n === 1 ? "" : "s"}`;

      const run =
        !Number.isFinite(interval) || interval <= 0
          ? "The reminder runs on schedule"
          : interval === 1
          ? "The reminder runs daily"
          : `The reminder runs every ${plural(interval, "day")}`;

      const late =
        !Number.isFinite(threshold) || threshold <= 0
          ? "a claim counts as “late” as soon as it is pending"
          : `a claim counts as “late” once it has been pending ${plural(
              threshold,
              "day"
            )}`;

      return `${run} (skipping days if no pending cases), and ${late} since its claim submit date.`;
    },
    // The Final Reminder is the only template shared across online and physical
    // events, so it gets the manual Online/Physical preview toggle.
    isFinalTemplate() {
      return !!this.activeTemplate && this.activeTemplate.templateKey === "final";
    },
    // Attachment chips shown in the Templates tab preview. For the per-type
    // templates the set is fixed by the template key (the *_online keys add the
    // Attendance Form). For the shared Final Reminder template the set follows
    // the Online/Physical toggle.
    previewAttachments() {
      if (!this.activeTemplate) return [];
      const key = this.activeTemplate.templateKey;
      const isOnline = this.isFinalTemplate
        ? this.attachmentPreviewOnline
        : key === "dynamic_online" || key === "revert_online";
      return this.attachmentStages.filter((a) => isOnline || !a.onlineOnly);
    },
    // The next 7 calendar days (YYYY-MM-DD), used as the Upcoming day tabs.
    upcomingDays() {
      const days = [];
      const base = moment().startOf("day");
      for (let i = 0; i < 7; i++) {
        days.push(base.clone().add(i, "day").format("YYYY-MM-DD"));
      }
      return days;
    },
    upcomingCountByDay() {
      const counts = {};
      for (const it of this.upcoming) {
        counts[it.scheduledDate] = (counts[it.scheduledDate] || 0) + 1;
      }
      return counts;
    },
    upcomingSelectedDate() {
      return this.upcomingDays[this.upcomingDayIndex];
    },
    // Reminders for the selected day, grouped by category (15 / 8 / 3 / Final).
    upcomingGroups() {
      const date = this.upcomingSelectedDate;
      const groups = {};
      for (const it of this.upcoming) {
        if (it.scheduledDate !== date) continue;
        if (!groups[it.category]) {
          groups[it.category] = {
            category: it.category,
            reminderType: it.reminderType,
            daysBefore: it.daysBefore,
            items: [],
          };
        }
        groups[it.category].items.push(it);
      }
      // Order: dynamic frequencies by daysBefore desc, Final reminder last.
      return Object.values(groups).sort((a, b) => {
        if (a.reminderType === "final" && b.reminderType !== "final") return 1;
        if (b.reminderType === "final" && a.reminderType !== "final") return -1;
        return b.daysBefore - a.daysBefore;
      });
    },
    logTotalPages() {
      return Math.max(Math.ceil(this.logCount / this.logLimit), 1);
    },
    blastHour: {
      get() {
        return (this.setting.sendTime || "09:30").split(":")[0];
      },
      set(v) {
        this.setting.sendTime = `${v}:${this.blastMinute}`;
      },
    },
    blastMinute: {
      get() {
        return (this.setting.sendTime || "09:30").split(":")[1];
      },
      set(v) {
        this.setting.sendTime = `${this.blastHour}:${v}`;
      },
    },
    timezoneList() {
      const list = [...this.timezoneOptions];
      if (this.setting.timezone && !list.includes(this.setting.timezone)) {
        list.unshift(this.setting.timezone);
      }
      return list;
    },
  },
  props: ["params"],
  data: () => ({
    model: new Model(),
    service: new Service(),
    serviceUrl: null,
    // Reminder campaign in focus. Re-scopes every tab below + every API call.
    campaign: "grant_application",
    campaigns: [
      {
        key: "grant_application",
        label: "Grant Application",
        desc: "Before the event - reminder for clients to apply Grant",
        party: "Clients",
        icon: "mdi-file-certificate-outline",
      },
      {
        key: "form_revert",
        label: "Form Revert",
        desc: "After the event - reminder for client to revert JD14 / T3 / Attendance Form",
        party: "Clients",
        icon: "mdi-file-restore-outline",
      },
      {
        key: "claim_followup",
        label: "Claim Follow-up",
        desc: "Daily reminder to HRDC Corp to expedite late claim approvals",
        party: "HRDC Corp",
        icon: "mdi-bank-outline",
      },
    ],
    tab: 0,
    tabs: [
      { label: "Templates", icon: "mdi-file-document-outline" },
      { label: "Frequencies", icon: "mdi-calendar-clock" },
      { label: "Schedule", icon: "mdi-clock-outline" },
      { label: "Insights", icon: "mdi-chart-box-outline" },
    ],
    hourOptions: Array.from({ length: 24 }, (_, i) =>
      String(i).padStart(2, "0")
    ),
    minuteOptions: Array.from({ length: 60 }, (_, i) =>
      String(i).padStart(2, "0")
    ),
    timezoneOptions: [
      "Asia/Kuala_Lumpur",
      "Asia/Singapore",
      "Asia/Jakarta",
      "Asia/Bangkok",
      "Asia/Ho_Chi_Minh",
      "Asia/Manila",
      "Asia/Hong_Kong",
      "Asia/Shanghai",
      "Asia/Taipei",
      "Asia/Tokyo",
      "Asia/Seoul",
      "Asia/Kolkata",
      "Asia/Dhaka",
      "Asia/Yangon",
      "Asia/Karachi",
      "Asia/Colombo",
      "Asia/Kathmandu",
      "Asia/Dubai",
      "Asia/Riyadh",
      "Asia/Tehran",
      "Asia/Jerusalem",
      "Australia/Perth",
      "Australia/Adelaide",
      "Australia/Brisbane",
      "Australia/Sydney",
      "Pacific/Auckland",
      "Pacific/Guam",
      "Pacific/Honolulu",
      "Europe/London",
      "Europe/Dublin",
      "Europe/Lisbon",
      "Europe/Paris",
      "Europe/Madrid",
      "Europe/Berlin",
      "Europe/Rome",
      "Europe/Amsterdam",
      "Europe/Brussels",
      "Europe/Zurich",
      "Europe/Stockholm",
      "Europe/Athens",
      "Europe/Helsinki",
      "Europe/Moscow",
      "Europe/Istanbul",
      "America/New_York",
      "America/Chicago",
      "America/Denver",
      "America/Los_Angeles",
      "America/Toronto",
      "America/Vancouver",
      "America/Mexico_City",
      "America/Sao_Paulo",
      "America/Argentina/Buenos_Aires",
      "Africa/Cairo",
      "Africa/Johannesburg",
      "Africa/Lagos",
      "Africa/Nairobi",
      "UTC",
    ],
    extensions: [
      History,
      Blockquote,
      Link,
      Underline,
      Strike,
      Italic,
      ListItem,
      BulletList,
      OrderedList,
      [Heading, { options: { levels: [1, 2, 3] } }],
      Bold,
      Code,
      HorizontalRule,
      Paragraph,
      HardBreak,
    ],
    templates: [],
    templateIndex: 0,
    // Illustrative attachment preview (Templates tab) - the chips shown atop the
    // email body. For the Online/Physical Event templates the set is fixed by
    // the template (Online adds the Attendance Form, stage 60). The Final
    // Reminder template is shared by both online and physical events, so it
    // exposes an Online/Physical toggle (attachmentPreviewOnline) to preview
    // either set. The backend composes the real email.
    attachmentPreviewOnline: true,
    attachmentStages: [
      { stageId: 7, label: "JD14 Form", file: "JD14_Form.pdf", onlineOnly: false },
      { stageId: 8, label: "T3 Form", file: "T3_Form.pdf", onlineOnly: false },
      {
        stageId: 60,
        label: "Attendance Form",
        file: "Attendance_Form.pdf",
        onlineOnly: true,
      },
    ],
    frequencies: [],
    setting: {
      uuid: null,
      cronEnabled: false,
      sendTime: "09:30",
      timezone: "Asia/Kuala_Lumpur",
      finalReminderDaysBefore: 1,
      // form_revert cadence (after the training end date).
      afterOffsetDays: 1,
      afterIntervalDays: 7,
      afterHorizonMonths: 6,
      // claim_followup digest.
      lateThresholdDays: 4,
      toRecipients: [],
      ccRecipients: [],
    },
    // Shown literally in the claim_followup template help text.
    casesTablePlaceholder: "{{{casesTable}}}",
    upcoming: [],
    upcomingDayIndex: 0,
    logs: [],
    logCount: 0,
    logPage: 1,
    logLimit: 25,
    previewDialog: false,
    previewHtml: "",
    previewTitle: "",
    emailPreview: {
      open: false,
      loading: false,
      title: "",
      hint: "",
      emails: [],
    },
    loading: {
      templates: false,
      frequencies: false,
      settingSave: false,
      upcoming: false,
      logs: false,
      runNow: false,
    },
  }),
  async created() {
    if (this.params && this.params.modelKey) {
      this.model.getByKey(this.params.modelKey);
      if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);
    }

    // Shared page - resolve to the first event module's service URL.
    if (
      !this.$service[this.service.key] ||
      this.$route.params.serviceKey === "hrdc_event_settings"
    ) {
      const { getAllEventConfigs } = require("@/services/hrdc_event/eventRegistry");
      const firstConfig = getAllEventConfigs()[0];
      if (firstConfig) this.service.set("key", firstConfig.serviceKey);
    }
    this.serviceUrl = this.$service[this.service.key];

    // Restore the active tab + Upcoming day from the URL so a page refresh
    // keeps the user where they were.
    const q = this.$route.query;
    if (q.campaign && this.campaigns.some((c) => c.key === q.campaign)) {
      this.campaign = q.campaign;
    }
    const t = parseInt(q.tab);
    if (Number.isInteger(t) && t >= 0 && t < this.tabs.length) this.tab = t;
    const d = parseInt(q.day);
    if (Number.isInteger(d) && d >= 0 && d < 7) this.upcomingDayIndex = d;

    await Promise.all([
      this.loadTemplates(),
      this.loadFrequencies(),
      this.loadSetting(),
      this.loadUpcoming(),
      this.loadLogs(),
    ]);
  },
  methods: {
    // Persist the active tab + Upcoming day in the URL (survives refresh).
    syncQuery() {
      this.$router
        .replace({
          query: {
            ...this.$route.query,
            campaign: this.campaign,
            tab: this.tab,
            day: this.upcomingDayIndex,
          },
        })
        .catch(() => {});
    },
    selectTab(i) {
      this.tab = i;
      this.syncQuery();
    },
    selectDay(i) {
      this.upcomingDayIndex = i;
      this.syncQuery();
    },
    // Switch reminder campaign - reset transient view state and reload every
    // campaign-scoped section for the new campaign.
    async selectCampaign(key) {
      if (this.campaign === key) return;
      this.campaign = key;
      this.templateIndex = 0;
      this.logPage = 1;
      this.attachmentPreviewOnline = true;
      this.syncQuery();
      await Promise.all([
        this.loadTemplates(),
        this.loadFrequencies(),
        this.loadSetting(),
        this.loadUpcoming(),
        this.loadLogs(),
      ]);
    },
    goBack() {
      this.$router
        .push({
          name: "PageServiceDashboard",
          params: { serviceKey: "hrdc_event_settings" },
        })
        .catch(() => {});
    },

    // ---- API helper -------------------------------------------------------
    async api(method, path, body) {
      const url = `${this.serviceUrl}/v1/en/console/email_reminder${path}`;
      const opts = {
        method,
        headers: { Authorization: `Bearer ${this.auth.token}` },
      };
      if (body !== undefined) {
        opts.headers["Content-Type"] = "application/json";
        opts.body = JSON.stringify(body);
      }
      const res = await fetch(url, opts);
      return res.json();
    },
    toast(message, type = "success") {
      this.$store.dispatch("showMessage", {
        message,
        messageType: type,
        timeout: 3500,
      });
    },

    // ---- Templates --------------------------------------------------------
    async loadTemplates() {
      this.loading.templates = true;
      try {
        const r = await this.api("GET", `/template/list?campaign=${this.campaign}`);
        if (r.status) {
          this.templates = r.data.map((t) => ({ ...t, _saving: false }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading.templates = false;
      }
    },
    async saveTemplate(tpl) {
      tpl._saving = true;
      try {
        const body = { uuid: tpl.uuid, emailContent: tpl.emailContent };
        // claim_followup has an admin-editable subject; other campaigns reuse
        // the confirmation email's subject so don't send one.
        if (this.isClaimFollowup) body.emailSubject = tpl.emailSubject || "";
        let r = await this.api("PUT", "/template/update", body);
        // claim_followup keeps its To/Cc recipients in this same card, so the
        // single Save button persists them alongside the template.
        if (r.status && this.isClaimFollowup) {
          r = await this.saveSetting({ silent: true });
        }
        this.toast(
          r.status ? `${tpl.name} saved.` : r.message || "Save failed.",
          r.status ? "success" : "error"
        );
      } catch (e) {
        console.error(e);
        this.toast("Save failed.", "error");
      } finally {
        tpl._saving = false;
      }
    },
    async previewTemplate(tpl) {
      this.emailPreview.title = `Preview — ${tpl.name}`;
      this.emailPreview.hint =
        "Full email preview with sample data — actual values are filled in when sent.";
      this.emailPreview.emails = [];
      this.emailPreview.loading = true;
      this.emailPreview.open = true;
      try {
        const r = await this.api("POST", "/preview-template", {
          emailContent: tpl.emailContent || "",
          emailSubject: tpl.emailSubject || "",
        });
        if (r.status) {
          const d = r.data || {};
          this.emailPreview.emails = [
            { subject: d.subject || "", html: d.html || "", note: null },
          ];
        } else {
          this.toast(r.message || "Preview failed.", "error");
          this.emailPreview.open = false;
        }
      } catch (e) {
        console.error(e);
        this.toast("Preview failed.", "error");
        this.emailPreview.open = false;
      } finally {
        this.emailPreview.loading = false;
      }
    },

    // ---- Frequencies ------------------------------------------------------
    async loadFrequencies() {
      this.loading.frequencies = true;
      try {
        const r = await this.api("GET", `/frequency/list?campaign=${this.campaign}`);
        if (r.status) {
          this.frequencies = r.data.map((f) => ({ ...f, _saving: false }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading.frequencies = false;
      }
    },
    addFrequencyRow() {
      this.frequencies.push({
        uuid: null,
        daysBefore: null,
        label: "",
        sortOrder: this.frequencies.length,
        isActive: true,
        _saving: false,
      });
    },
    async saveFrequency(f, index) {
      const days = parseInt(f.daysBefore);
      if (!Number.isInteger(days) || days < 1) {
        this.toast("Days before must be a positive number.", "error");
        return;
      }
      f._saving = true;
      try {
        let r;
        if (f.uuid) {
          r = await this.api("PUT", "/frequency/update", {
            uuid: f.uuid,
            daysBefore: days,
            label: f.label,
            sortOrder: f.sortOrder,
            isActive: f.isActive,
          });
        } else {
          r = await this.api("POST", "/frequency/create", {
            campaign: this.campaign,
            daysBefore: days,
            label: f.label,
            sortOrder: index,
          });
        }
        if (r.status) {
          this.toast("Frequency saved.");
          await this.loadFrequencies();
        } else {
          this.toast(r.message || "Save failed.", "error");
        }
      } catch (e) {
        console.error(e);
        this.toast("Save failed.", "error");
      } finally {
        f._saving = false;
      }
    },
    async removeFrequency(f, index) {
      if (!f.uuid) {
        this.frequencies.splice(index, 1);
        return;
      }
      try {
        const r = await this.api("DELETE", `/frequency/deactivate/${f.uuid}`);
        if (r.status) {
          this.toast("Frequency removed.");
          await this.loadFrequencies();
        } else {
          this.toast(r.message || "Remove failed.", "error");
        }
      } catch (e) {
        console.error(e);
        this.toast("Remove failed.", "error");
      }
    },

    // ---- Setting ----------------------------------------------------------
    async loadSetting() {
      try {
        const r = await this.api("GET", `/setting?campaign=${this.campaign}`);
        if (r.status && r.data) {
          this.setting = {
            uuid: r.data.uuid,
            cronEnabled: r.data.cronEnabled,
            sendTime: r.data.sendTime || "09:30",
            timezone: r.data.timezone || "Asia/Kuala_Lumpur",
            finalReminderDaysBefore: r.data.finalReminderDaysBefore,
            afterOffsetDays:
              r.data.afterOffsetDays != null ? r.data.afterOffsetDays : 1,
            afterIntervalDays:
              r.data.afterIntervalDays != null ? r.data.afterIntervalDays : 7,
            afterHorizonMonths:
              r.data.afterHorizonMonths != null ? r.data.afterHorizonMonths : 6,
            lateThresholdDays:
              r.data.lateThresholdDays != null ? r.data.lateThresholdDays : 4,
            toRecipients: Array.isArray(r.data.toRecipients)
              ? r.data.toRecipients
              : [],
            ccRecipients: Array.isArray(r.data.ccRecipients)
              ? r.data.ccRecipients
              : [],
          };
        }
      } catch (e) {
        console.error(e);
      }
    },
    // Trim, drop blanks, and de-dupe a chip-input recipient array before saving.
    cleanRecipients(list) {
      const out = [];
      for (const item of list || []) {
        const email = String(item).trim();
        if (email && !out.includes(email)) out.push(email);
      }
      return out;
    },
    // `silent` suppresses the standalone toast so a caller (e.g. saveTemplate
    // for claim_followup) can roll this into its own combined success message.
    async saveSetting({ silent = false } = {}) {
      if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(this.setting.sendTime)) {
        this.toast("Blast time must be in HH:mm format.", "error");
        return { status: false };
      }
      this.loading.settingSave = true;
      try {
        const body = {
          campaign: this.campaign,
          cronEnabled: this.setting.cronEnabled,
          sendTime: this.setting.sendTime,
          timezone: this.setting.timezone,
        };
        if (this.isFormRevert) {
          body.afterOffsetDays = parseInt(this.setting.afterOffsetDays);
          body.afterIntervalDays = parseInt(this.setting.afterIntervalDays);
          body.afterHorizonMonths = parseInt(this.setting.afterHorizonMonths);
        } else if (this.isClaimFollowup) {
          body.afterIntervalDays = parseInt(this.setting.afterIntervalDays);
          body.lateThresholdDays = parseInt(this.setting.lateThresholdDays);
          body.toRecipients = this.cleanRecipients(this.setting.toRecipients);
          body.ccRecipients = this.cleanRecipients(this.setting.ccRecipients);
        } else {
          body.finalReminderDaysBefore = parseInt(
            this.setting.finalReminderDaysBefore
          );
        }
        const r = await this.api("PUT", "/setting/update", body);
        if (!silent) {
          this.toast(
            r.status ? "Settings saved." : r.message || "Save failed.",
            r.status ? "success" : "error"
          );
        }
        return r;
      } catch (e) {
        console.error(e);
        if (!silent) this.toast("Save failed.", "error");
        return { status: false };
      } finally {
        this.loading.settingSave = false;
      }
    },
    async runNow() {
      this.loading.runNow = true;
      try {
        const r = await this.api("POST", "/run-now", { campaign: this.campaign });
        if (r.status) {
          const s = r.data || {};
          this.toast(
            `Run complete - ${s.sent || 0} sent, ${s.failed || 0} failed, ${
              s.skipped || 0
            } skipped.`
          );
          await Promise.all([this.loadUpcoming(), this.loadLogs()]);
        } else {
          this.toast(r.message || "Run failed.", "error");
        }
      } catch (e) {
        console.error(e);
        this.toast("Run failed.", "error");
      } finally {
        this.loading.runNow = false;
      }
    },

    // ---- History ----------------------------------------------------------
    async loadUpcoming() {
      this.loading.upcoming = true;
      try {
        const r = await this.api("GET", `/upcoming?campaign=${this.campaign}`);
        if (r.status) {
          this.upcoming = r.data || [];
          // Keep the selected day on refresh; clamp if out of range.
          if (this.upcomingDayIndex > 6) this.upcomingDayIndex = 0;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading.upcoming = false;
      }
    },
    // Preview the actual email(s) that would be sent on a given day, rendered
    // with real data (the cases embedded in {{casesTable}} for the digest, or
    // the per-application email for the other campaigns).
    async previewUpcomingDay(date) {
      this.emailPreview.title = `Preview — ${this.upcomingDayLabel(
        date
      )} (${this.formatDayShort(date)})`;
      this.emailPreview.hint = "";
      this.emailPreview.emails = [];
      this.emailPreview.loading = true;
      this.emailPreview.open = true;
      try {
        const r = await this.api(
          "GET",
          `/upcoming/preview?campaign=${this.campaign}&date=${date}`
        );
        if (r.status) {
          const emails = (r.data && r.data.emails) || [];
          this.emailPreview.emails = emails;
          this.emailPreview.hint = emails.length
            ? `${emails.length} email${
                emails.length > 1 ? "s" : ""
              } would be sent — exactly as shown below (preview only, nothing is sent).`
            : "";
        } else {
          this.toast(r.message || "Preview failed.", "error");
          this.emailPreview.open = false;
        }
      } catch (e) {
        console.error(e);
        this.toast("Preview failed.", "error");
        this.emailPreview.open = false;
      } finally {
        this.emailPreview.loading = false;
      }
    },
    async loadLogs() {
      this.loading.logs = true;
      try {
        const r = await this.api(
          "GET",
          `/log/list?campaign=${this.campaign}&page=${this.logPage}&limit=${this.logLimit}`
        );
        if (r.status) {
          this.logs = r.data || [];
          this.logCount = r.count || 0;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading.logs = false;
      }
    },
    changeLogPage(delta) {
      const next = this.logPage + delta;
      if (next < 1 || next > this.logTotalPages) return;
      this.logPage = next;
      this.loadLogs();
    },
    viewLog(log) {
      this.previewTitle = `${log.category} Reminder - ${log.emailSubject || ""}`;
      this.previewHtml = log.emailContent || "<em>No content recorded.</em>";
      this.previewDialog = true;
    },

    // ---- Formatters -------------------------------------------------------
    joinRecipients(list) {
      if (!list || !Array.isArray(list) || !list.length) return "-";
      return list.join(", ");
    },
    catClass(reminderType) {
      if (reminderType === "final") return "er-cat--final";
      if (reminderType === "revert") return "er-cat--revert";
      if (reminderType === "digest") return "er-cat--digest";
      return "er-cat--dynamic";
    },
    // A template counts as "configured" once its body has real (non-tag) text.
    isTemplateSet(tpl) {
      if (!tpl || !tpl.emailContent) return false;
      return tpl.emailContent.replace(/<[^>]*>/g, "").trim().length > 0;
    },
    formatDate(d) {
      return d ? moment(d).format("ddd, D MMM YYYY") : "-";
    },
    formatDateTime(d) {
      return d ? moment(d).format("D MMM YYYY, HH:mm") : "-";
    },
    formatDayShort(d) {
      return d ? moment(d).format("D MMM") : "";
    },
    upcomingDayLabel(date) {
      const diff = moment(date)
        .startOf("day")
        .diff(moment().startOf("day"), "days");
      if (diff === 0) return "Today";
      if (diff === 1) return "Tomorrow";
      return moment(date).format("ddd");
    },
    groupTitle(grp) {
      if (grp.reminderType === "final") {
        return "Final reminder before the event date";
      }
      if (grp.reminderType === "revert") {
        return `Reminder - ${grp.daysBefore} day(s) after the end date`;
      }
      if (grp.reminderType === "digest") {
        return "Claims pending approval past the late threshold";
      }
      return `Reminder - ${grp.daysBefore} day(s) before the event`;
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .er-sub-header__title {
  color: var(--app-text);
}

.dark-mode .er-sub-header__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .er-back-btn {
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .er-back-btn:hover {
  background: var(--app-elevated);
}

.dark-mode .er-campaign-bar {
  border-top: 1px solid var(--app-border);
}

.dark-mode .er-campaign-bar__label {
  color: var(--app-text-faint);
}

.dark-mode .er-campaign-bar__desc {
  color: var(--app-text-faint);
}

.dark-mode .er-tabbar {
  background: var(--app-elevated);
}

.dark-mode .er-tabbar__tab {
  color: var(--app-text-muted);
}

.dark-mode .er-tabbar__tab:hover {
  color: var(--app-text);
}

.dark-mode .er-tabbar__tab--active {
  background: var(--app-card);
}

.dark-mode .er-hint {
  background: var(--app-accent-soft);
  color: var(--app-neutral-fg);
}

.dark-mode .er-hint code {
  background: var(--app-accent-soft);
}

.dark-mode .er-card {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .er-card__icon {
  background: var(--app-accent-soft);
}

.dark-mode .er-card__icon--green {
  background: var(--app-success-bg);
}

.dark-mode .er-card__icon--amber {
  background: var(--app-warning-bg);
}

.dark-mode .er-card__title {
  color: var(--app-text);
}

.dark-mode .er-card__actions {
  border-top: 1px solid var(--app-border);
}

.dark-mode .er-tplpick {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .er-tplpick:hover {
  border-color: var(--app-border-strong);
}

.dark-mode .er-tplpick--active {
  background: var(--app-accent-soft);
}

.dark-mode .er-tplpick__icon {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .er-tplpick__name {
  color: var(--app-text);
}

.dark-mode .er-tplpick__status--empty {
  color: var(--app-text-faint);
}

.dark-mode .er-atch-seg {
  background: var(--app-elevated);
  border: 1px solid var(--app-border);
}

.dark-mode .er-atch-seg__btn {
  color: var(--app-text-muted);
}

.dark-mode .er-atch-seg__btn--active {
  background: var(--app-card);
}

.dark-mode .er-mailatch {
  border: 1px solid var(--app-border);
  background: var(--app-surface);
}

.dark-mode .er-mailatch__label {
  color: var(--app-text-muted);
}

.dark-mode .er-mailchip {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .er-mailchip__name {
  color: var(--app-text);
}

.dark-mode .er-label {
  color: var(--app-text-muted);
}

.dark-mode .er-label--section {
  color: var(--app-text);
  border-top: 1px solid var(--app-border);
}

.dark-mode .er-text {
  color: var(--app-text-muted);
}

.dark-mode .er-editor {
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .er-chip {
  background: var(--app-accent-soft);
}

.dark-mode .er-tag {
  color: var(--app-text-muted);
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

/* Category / status pills. The light rules pair a pale wash with a DARK
   ink; in dark mode only the wash flips, so the ink has to flip with it —
   otherwise the label sits dark-on-dark and reads as an empty pill. The 1px
   edge is an inset shadow rather than a border so the badge keeps the exact
   same box in both themes and table rows don't shift. */
.dark-mode .er-cat--dynamic {
  background: var(--app-purple-bg);
  color: var(--app-purple-fg);
  box-shadow: inset 0 0 0 1px var(--app-purple-border);
}

.dark-mode .er-cat--final {
  background: var(--app-warning-bg);
  color: var(--app-warning-fg);
  box-shadow: inset 0 0 0 1px var(--app-warning-border);
}

.dark-mode .er-cat--revert {
  background: var(--app-teal-bg);
  color: var(--app-teal-fg);
  box-shadow: inset 0 0 0 1px var(--app-teal-border);
}

.dark-mode .er-cat--digest {
  background: var(--app-orange-bg);
  color: var(--app-orange-fg);
  box-shadow: inset 0 0 0 1px var(--app-orange-border);
}

.dark-mode .er-status--on {
  background: var(--app-success-bg);
  color: var(--app-success-fg);
  box-shadow: inset 0 0 0 1px var(--app-success-border);
}

.dark-mode .er-status--off {
  background: var(--app-elevated);
  color: var(--app-text-faint);
  box-shadow: inset 0 0 0 1px var(--app-border);
}

.dark-mode .er-status--fail {
  background: var(--app-danger-bg);
  color: var(--app-danger-fg);
  box-shadow: inset 0 0 0 1px var(--app-danger-border);
}

.dark-mode .er-btn--ghost {
  background: var(--app-surface);
  color: var(--app-neutral-fg);
  border-color: var(--app-border);
}

.dark-mode .er-btn--outline {
  background: var(--app-card);
}

.dark-mode .er-btn--outline:hover:not(:disabled) {
  background: var(--app-accent-soft);
}

.dark-mode .er-action-btn {
  color: var(--app-neutral-fg);
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

/* Hover has to be re-stated for dark: the light rule
   (.er-action-btn:hover:not(:disabled)) out-specifies the plain .dark-mode
   override above, so without this the button flashes to a near-white
   #f0f7ff fill with the dark theme's pale icon on top of it. */
.dark-mode .er-action-btn:hover:not(:disabled) {
  background: var(--app-accent-soft);
  border-color: var(--app-accent-border);
  color: var(--app-accent-strong);
}

.dark-mode .er-action-btn:hover:not(:disabled) .v-icon {
  color: var(--app-accent-strong) !important;
}

.dark-mode .er-action-btn--danger:hover:not(:disabled) {
  background: var(--app-danger-bg);
  border-color: var(--app-danger-border);
  color: var(--app-danger-fg);
}

.dark-mode .er-action-btn--danger:hover:not(:disabled) .v-icon {
  color: var(--app-danger-fg) !important;
}

/* Same story for the ghost/outline buttons — the light hover fills white. */
.dark-mode .er-btn--ghost:hover:not(:disabled) {
  background: var(--app-accent-soft);
  border-color: var(--app-accent-border);
  color: var(--app-accent-strong);
}

.dark-mode .er-btn--outline {
  color: var(--app-accent-strong);
  border-color: var(--app-accent-border);
}

.dark-mode .er-loading__text {
  color: var(--app-text-muted);
}

.dark-mode .er-empty {
  color: var(--app-text-faint);
}

.dark-mode .er-toggle-label {
  color: var(--app-neutral-fg);
}

.dark-mode .er-cadence__summary {
  background: var(--app-accent-soft);
  color: var(--app-neutral-fg);
}

.dark-mode .er-field-icon {
  background: var(--app-accent-soft);
}

.dark-mode .er-timefield__sep {
  color: var(--app-text-faint);
}

.dark-mode .er-table th {
  color: var(--app-text-faint);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .er-table td {
  color: var(--app-text);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .er-table tbody tr:hover {
  background: var(--app-surface);
}

.dark-mode .er-td-strong {
  color: var(--app-text);
}

.dark-mode .er-td-muted {
  color: var(--app-text-muted);
}

/* Upcoming day strip. Light mode stacks a #f8fafc tab over a white card —
   the tab reads as a RAISED tile. Mapping that literally to dark put
   --app-surface (#272c34) on --app-card (#2c313a), i.e. the tab came out
   DARKER than the card behind it, so the whole strip sank into the panel and
   the row read as a dark hole with faint text in it. The tiers are inverted
   here on purpose: the tab lifts to --app-elevated, and the Preview action
   below it drops to --app-surface, so the pair reads as tile + attached
   action instead of two similar dark bars. */
.dark-mode .er-daytab {
  background: var(--app-elevated);
  border: 1px solid var(--app-border);
}

.dark-mode .er-daytab:hover {
  border-color: var(--app-border-strong);
}

.dark-mode .er-daytab--active {
  background: var(--app-accent-soft);
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-ring);
}

.dark-mode .er-daytab__label {
  color: var(--app-text);
}

/* The light theme names the selected day in #0369a1. That rule lives in the
   light block BELOW this one at equal specificity, so it won it outright and
   "Today" was rendering as dark blue on the dark tab. */
.dark-mode .er-daytab--active .er-daytab__label {
  color: var(--app-accent-strong);
}

.dark-mode .er-daytab__date {
  color: var(--app-text-muted);
}

/* Counts. The non-zero badge inherits the light theme's #0ea5e9 fill, which
   is too dim to carry white numerals on a dark tile — brighten the fill and
   flip the numerals to the dark-on-accent ink. */
.dark-mode .er-daytab__count {
  background: var(--app-accent);
  color: var(--app-accent-fg);
}

.dark-mode .er-daytab__count--zero {
  background: var(--app-surface);
  color: var(--app-text-muted);
  box-shadow: inset 0 0 0 1px var(--app-border);
}

/* Preview (per-day). The accent WASH was the problem: a sky tint at 14% over
   the card composites to a murky navy that is only a few levels off the tab
   above it, so the button had no edge of its own and its 11px label had
   nothing to sit against. Dropping to the plain --app-surface tier gives the
   same #7dd3fc label ~8:1 instead of ~6:1 and, more importantly, separates
   the action from the tile. The accent is spent on the text and on hover,
   not on a fill that only darkens the row. Matches .er-action-btn, which is
   the same control one row up (Refresh). */
.dark-mode .er-daytab__preview {
  background: var(--app-surface);
  border-color: var(--app-border);
  color: var(--app-accent-strong);
}

.dark-mode .er-daytab__preview .v-icon {
  color: var(--app-accent-strong) !important;
}

.dark-mode .er-daytab__preview:hover {
  background: var(--app-accent-soft-strong);
  border-color: var(--app-accent);
  color: var(--app-text);
}

.dark-mode .er-daytab__preview:hover .v-icon {
  color: var(--app-text) !important;
}

.dark-mode .er-cgroup__head {
  border-bottom: 2px solid var(--app-border);
}

.dark-mode .er-cgroup__title {
  color: var(--app-neutral-fg);
}

.dark-mode .er-cgroup__count {
  background: var(--app-accent-soft);
}

.dark-mode .er-pagination {
  border-top: 1px solid var(--app-border);
}

.dark-mode .er-pagination__total {
  color: var(--app-text-faint);
}

.dark-mode .er-pagination__label {
  color: var(--app-text-muted);
}

.dark-mode .er-modal {
  background: var(--app-card);
}

.dark-mode .er-modal__head {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .er-modal__title {
  color: var(--app-text);
}

.dark-mode .er-modal__body {
  color: var(--app-text);
}

.dark-mode .er-preview-hint {
  background: var(--app-accent-soft);
}

.dark-mode .er-preview-email {
  border: 1px solid var(--app-border);
}

.dark-mode .er-preview-meta {
  background: var(--app-surface);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .er-preview-k {
  color: var(--app-text-faint);
}

.dark-mode .er-preview-v {
  color: var(--app-text);
}

.dark-mode .er-preview-note {
  background: var(--app-warning-bg);
}

.dark-mode .er-preview-html {
  color: var(--app-text);
}

@media (max-width: 599px) {
  .dark-mode .er-table--form tr {
    border: 1px solid var(--app-border);
  }

  .dark-mode .er-table--form td[data-label]::before {
    color: var(--app-text-faint);
  }
}

/* >>> end auto-dark <<< */

/* ============================================
   Dark mode — page shell
   The page drops to the ground tier so the header card and the .er-card stack
   read as raised surfaces, the same page < card relationship the light palette
   has (#d8dce2 / #fff).
   ============================================ */
.dark-mode .er-wrap {
  background-color: var(--app-page);
}

.dark-mode .er-head {
  background: var(--app-card);
  box-shadow: 0 1px 4px var(--app-shadow-soft);
}

.dark-mode .er-head__back {
  background: var(--app-page);
  border-color: var(--app-border);
}

.dark-mode .er-head__back:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.er-wrap {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer, so short tabs (Schedule) don't
     leave a strip of bare main under the page. See .layout-main__content in
     LayoutConsoleV2.vue. */
  flex: 1 0 auto;
  padding: 36px 44px 60px !important;
  font-family: "Lato", sans-serif;
}

/* ---- Header card: sub-header + campaign row ---- */
.er-head {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04), 0 4px 20px rgba(0, 0, 0, 0.03);
  padding: 18px 22px;
  margin-bottom: 20px;
}

.er-head__back {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}
.er-head__back:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
  transform: translateX(-2px);
}

/* ---- Sub-header ---- */
.er-sub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.er-sub-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.er-sub-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}
.er-sub-header__subtitle {
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 2px 0 0;
}
.er-back-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}
.er-back-btn:hover {
  background: #f1f5f9;
}

/* ---- Reminder campaign dropdown: second row of the header card ---- */
.er-campaign-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  border-top: 1px solid #eef2f7;
  /* Negative inline margins bleed the divider to the card's full width while
     the content stays on the card's own 22px gutter. */
  margin: 16px -22px 0;
  padding: 16px 22px 0;
  flex-wrap: wrap;
}
.er-campaign-bar__label {
  font-size: 0.66rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #94a3b8;
  flex-shrink: 0;
}
.er-campaign-bar__desc {
  flex: 1;
  min-width: 180px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #94a3b8;
  line-height: 1.5;
}

/* ---- Segmented tab bar ---- */
.er-tabbar {
  display: inline-flex;
  gap: 4px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 10px;
  margin-bottom: 22px;
}
.er-tabbar__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 7px;
  border: none;
  background: transparent;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
}
.er-tabbar__tab:hover {
  color: #0f172a;
}
.er-tabbar__tab--active {
  background: #fff;
  color: #0ea5e9;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* ---- Panel ---- */
.er-panel {
  display: flex;
  flex-direction: column;
}

/* ---- Hint banner ---- */
.er-hint {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 0.78rem;
  color: #475569;
  line-height: 1.6;
  margin-bottom: 18px;
}
.er-hint code {
  background: #e0f2fe;
  color: #0369a1;
  padding: 1px 6px;
  border-radius: 5px;
  font-size: 0.72rem;
  font-family: "SF Mono", "Fira Code", monospace;
  margin: 0 2px;
  display: inline-block;
}

/* ---- Card ---- */
.er-card {
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 18px;
  transition: box-shadow 0.2s ease;
}
.er-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
}
.er-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.er-card__head-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.er-card__icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #f0f9ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.er-card__icon--green {
  background: #ecfdf5;
}
.er-card__icon--amber {
  background: #fffbeb;
}
.er-card__title {
  font-size: 0.98rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}
.er-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}
.er-card__actions--start {
  justify-content: flex-start;
}

/* ---- Template picker ---- */
.er-tplpicker {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.er-tplpick {
  flex: 1;
  min-width: 210px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
}
.er-tplpick:hover {
  border-color: #94a3b8;
}
.er-tplpick--active {
  background: #f0f9ff;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}
.er-tplpick__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.er-tplpick--active .er-tplpick__icon {
  border-color: #bae6fd;
}
.er-tplpick__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.er-tplpick__name {
  font-size: 0.86rem;
  font-weight: 800;
  color: #0f172a;
}
.er-tplpick--active .er-tplpick__name {
  color: #0369a1;
}
.er-tplpick__status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.er-tplpick__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.er-tplpick__status--set {
  color: #059669;
}
.er-tplpick__status--empty {
  color: #94a3b8;
}

/* ---- Attachment preview (rendered atop the email body) ---- */
.er-atch-seg {
  display: inline-flex;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 3px;
  gap: 3px;
}
.er-atch-seg__btn {
  border: none;
  background: transparent;
  font-size: 0.76rem;
  font-weight: 700;
  color: #64748b;
  padding: 5px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.er-atch-seg--sm .er-atch-seg__btn {
  font-size: 0.7rem;
  padding: 3px 10px;
}
.er-atch-seg__btn--active {
  background: #ffffff;
  color: #0369a1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.er-mailatch {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  padding: 10px 12px;
  margin-bottom: 10px;
}
.er-mailatch__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.er-mailatch__label {
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
}
.er-mailatch__bar .er-atch-seg {
  margin-left: auto;
}
.er-mailatch__files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.er-mailchip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 10px;
}
.er-mailchip__name {
  font-size: 0.78rem;
  font-weight: 700;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---- Text / labels ---- */
.er-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #64748b;
  margin: 14px 0 6px;
}
/* Sub-section heading within a card (e.g. "Email Recipient" in the template
   editor) - slightly stronger, with a divider from the section above. */
.er-label--section {
  font-size: 0.8rem;
  color: #0f172a;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}
.er-text {
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 6px;
}
.er-editor {
  border: 1.5px solid #d1d5db;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.15s ease;
  margin-bottom: 2px;
}
.er-editor:focus-within {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.12);
}

/* ---- Chips ---- */
.er-chip {
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #0369a1;
  background: #e0f2fe;
  padding: 3px 9px;
  border-radius: 20px;
  font-family: "SF Mono", "Fira Code", monospace;
}
.er-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 3px 9px;
  border-radius: 6px;
}
.er-cat {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}
.er-cat--dynamic {
  color: #7c3aed;
  background: #f3e8ff;
}
.er-cat--final {
  color: #b45309;
  background: #fef3c7;
}
.er-cat--revert {
  color: #0f766e;
  background: #ccfbf1;
}
.er-cat--digest {
  color: #9a3412;
  background: #ffedd5;
}

/* ---- Status pill ---- */
.er-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 4px 11px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.er-status--sm {
  padding: 3px 9px;
  font-size: 0.66rem;
}
.er-status__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.er-status--on {
  background: #ecfdf5;
  color: #059669;
}
.er-status--off {
  background: #f1f5f9;
  color: #94a3b8;
}
.er-status--fail {
  background: #fef2f2;
  color: #dc2626;
}

/* ---- Buttons ---- */
.er-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid transparent;
}
.er-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.er-btn--primary {
  background: #0ea5e9;
  color: #fff;
}
.er-btn--primary:hover:not(:disabled) {
  background: #0284c7;
}
.er-btn--ghost {
  background: #f8fafc;
  color: #475569;
  border-color: #e2e8f0;
}
.er-btn--ghost:hover:not(:disabled) {
  background: #f0f7ff;
  border-color: #0ea5e9;
  color: #0ea5e9;
}
.er-btn--outline {
  background: #fff;
  color: #0ea5e9;
  border-color: #0ea5e9;
}
.er-btn--outline:hover:not(:disabled) {
  background: #f0f9ff;
}

.er-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 11px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.er-action-btn:hover:not(:disabled) {
  background: #f0f7ff;
  border-color: #0ea5e9;
  color: #0ea5e9;
}
.er-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.er-action-btn--danger:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #ef4444;
  color: #ef4444;
}
.er-action-btn--xs {
  padding: 4px 9px;
  font-size: 0.72rem;
}

/* ---- Loading / empty ---- */
.er-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  padding: 32px;
}
.er-loading__text {
  font-size: 0.85rem;
  color: #64748b;
}
.er-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 36px;
  font-size: 0.82rem;
  color: #94a3b8;
}

/* ---- Inline rows / grid ---- */
.er-inline-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.er-toggle-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 4px 0 10px;
}
.er-toggle-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #475569;
  user-select: none;
}
.er-grid {
  display: flex;
  gap: 28px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

/* ---- form_revert cadence fields ---- */
.er-cadence {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 6px;
}
.er-cadence__field {
  display: flex;
  flex-direction: column;
}
.er-cadence__field .er-label {
  margin-top: 0;
}
.er-cadence__summary {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 14px;
  padding: 10px 14px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
  line-height: 1.6;
}

/* ---- Time / select field row ---- */
.er-timefield {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.er-field-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f0f9ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.er-timefield__sep {
  font-size: 1.05rem;
  font-weight: 800;
  color: #cbd5e1;
}

/* ---- Tables ---- */
.er-table-scroll {
  overflow-x: auto;
}
.er-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 6px;
}
.er-table th {
  text-align: left;
  font-size: 0.66rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #94a3b8;
  padding: 9px 10px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}
.er-table td {
  font-size: 0.79rem;
  color: #334155;
  padding: 9px 10px;
  border-bottom: 1px solid #f1f5f9;
}
.er-table tbody tr:hover {
  background: #f8fafc;
}
.er-table--form tbody tr:hover {
  background: transparent;
}
.er-td-strong {
  font-weight: 700;
  color: #0f172a;
}
.er-td-muted {
  color: #64748b;
}
.er-table__actions {
  white-space: nowrap;
}
.er-row-actions {
  display: flex;
  gap: 6px;
}

/* ---- Upcoming day tabs ---- */
.er-daytabs {
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
  overflow-x: auto;
  padding-bottom: 4px;
}
.er-daytab {
  position: relative;
  flex: 1 0 auto;
  min-width: 104px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 30px 10px 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
}
.er-daytab:hover {
  border-color: #94a3b8;
}
.er-daytab--active {
  background: #f0f9ff;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}
.er-daytab__label {
  display: block;
  font-size: 0.8rem;
  font-weight: 800;
  color: #0f172a;
}
.er-daytab--active .er-daytab__label {
  color: #0369a1;
}
.er-daytab__date {
  display: block;
  font-size: 0.69rem;
  font-weight: 600;
  color: #94a3b8;
  margin-top: 1px;
}
.er-daytab__count {
  position: absolute;
  top: 8px;
  right: 8px;
  min-width: 19px;
  height: 19px;
  padding: 0 5px;
  border-radius: 10px;
  background: #0ea5e9;
  color: #fff;
  font-size: 0.66rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.er-daytab__count--zero {
  background: #e2e8f0;
  color: #94a3b8;
}
.er-daytab-wrap {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1 0 auto;
  min-width: 104px;
}
.er-daytab-wrap .er-daytab {
  flex: 0 0 auto;
  width: 100%;
}
.er-daytab__preview {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  font-size: 0.68rem;
  font-weight: 700;
  color: #0369a1;
  background: #f0f9ff;
  border: 1px solid #e0f2fe;
  border-radius: 8px;
  padding: 4px 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.er-daytab__preview:hover {
  background: #e0f2fe;
  border-color: #0ea5e9;
}

/* ---- Category groups (within a day) ---- */
.er-cgroup {
  margin-bottom: 16px;
}
.er-cgroup:last-child {
  margin-bottom: 0;
}
.er-cgroup__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 2px;
  border-bottom: 2px solid #e2e8f0;
}
.er-cgroup__title {
  font-size: 0.79rem;
  font-weight: 700;
  color: #475569;
}
.er-cgroup__count {
  margin-left: auto;
  font-size: 0.68rem;
  font-weight: 800;
  color: #0369a1;
  background: #e0f2fe;
  padding: 2px 9px;
  border-radius: 20px;
}

/* ---- Pagination ---- */
.er-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}
.er-pagination__total {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: #94a3b8;
}
.er-pagination__nav {
  display: flex;
  align-items: center;
  gap: 12px;
}
.er-pagination__label {
  font-size: 0.76rem;
  font-weight: 600;
  color: #64748b;
}

/* ---- Preview modal ---- */
.er-modal {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
}
.er-modal__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e8eaed;
}
.er-modal__title {
  font-size: 0.92rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}
.er-modal__body {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
  font-size: 0.85rem;
  color: #334155;
  line-height: 1.6;
}

/* ---- Upcoming day preview (structured email list) ---- */
.er-preview-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.76rem;
  font-weight: 600;
  color: #0369a1;
  background: #f0f9ff;
  border: 1px solid #e0f2fe;
  border-radius: 8px;
  padding: 8px 12px;
  margin: 0 0 16px;
}
.er-preview-email {
  border: 1px solid #e8eaed;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
}
.er-preview-email:last-child {
  margin-bottom: 0;
}
.er-preview-meta {
  background: #f8fafc;
  border-bottom: 1px solid #e8eaed;
  padding: 10px 14px;
}
.er-preview-row {
  display: flex;
  gap: 8px;
  font-size: 0.78rem;
  line-height: 1.5;
}
.er-preview-row + .er-preview-row {
  margin-top: 2px;
}
.er-preview-k {
  flex: 0 0 56px;
  font-weight: 800;
  color: #94a3b8;
  text-transform: uppercase;
  font-size: 0.66rem;
  letter-spacing: 0.3px;
  padding-top: 2px;
}
.er-preview-v {
  font-weight: 600;
  color: #334155;
  word-break: break-word;
}
.er-preview-note {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.74rem;
  font-weight: 600;
  color: #b45309;
  background: #fffbeb;
  border-bottom: 1px solid #fef3c7;
  padding: 8px 14px;
}
.er-preview-html {
  padding: 18px;
  font-size: 0.82rem;
  color: #334155;
  line-height: 1.6;
}

@media (max-width: 599px) {
  .er-wrap {
    padding: 20px 14px 40px !important;
  }
  .er-head {
    padding: 16px;
    margin-bottom: 14px;
  }
  .er-campaign-bar {
    margin: 14px -16px 0;
    padding: 14px 16px 0;
  }
  .er-tabbar {
    display: flex;
    width: 100%;
  }
  .er-tabbar__tab {
    flex: 1;
    justify-content: center;
    padding: 7px 6px;
  }

  /* Frequencies tab - the editable table can't fit a phone row (number +
     label inputs + checkbox + two buttons), so each row becomes a stacked,
     labelled card. The column headers are hidden and re-shown per field via
     the cells' data-label attributes. */
  .er-table--form,
  .er-table--form tbody,
  .er-table--form tr,
  .er-table--form td {
    display: block;
    width: 100%;
  }
  .er-table--form thead {
    display: none;
  }
  .er-table--form tr {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 12px;
  }
  .er-table--form td {
    padding: 6px 0;
    border-bottom: none;
  }
  .er-table--form td[data-label]::before {
    content: attr(data-label);
    display: block;
    font-size: 0.66rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #94a3b8;
    margin-bottom: 4px;
  }
  /* Action buttons share the row's full width so they're easy to tap. */
  .er-table--form .er-row-actions {
    gap: 8px;
  }
  .er-table--form .er-row-actions .er-action-btn {
    flex: 1;
    justify-content: center;
  }

  /* Final Reminder: let the field + Save button wrap rather than overflow. */
  .er-inline-row {
    flex-wrap: wrap;
  }
}
</style>

<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .er-wrap .er-input.v-text-field--outlined > .v-input__control > .v-input__slot {
  background: var(--app-card) !important;
}

.dark-mode .er-wrap .er-input.v-text-field--outlined fieldset {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .er-wrap .er-input.v-text-field--outlined:hover fieldset {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .er-wrap .er-input.v-input--is-focused > .v-input__control > .v-input__slot {
  background: var(--app-card) !important;
}

.dark-mode .er-wrap .er-input .v-text-field__slot input::placeholder {
  color: var(--app-text-faint) !important;
}

.dark-mode .er-wrap .er-recipients .v-chip {
  color: var(--app-text) !important;
}

.dark-mode .er-wrap .er-recipients .v-chip .v-chip__close {
  color: var(--app-text-faint) !important;
}

.dark-mode .er-wrap .er-recipients input::placeholder {
  color: var(--app-text-faint) !important;
}

.dark-mode .er-wrap .er-checkbox.v-input--checkbox {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .er-wrap .er-checkbox.v-input--checkbox:hover {
  border-color: var(--app-border-strong);
}

.dark-mode .er-wrap .er-checkbox.v-input--checkbox .v-label {
  color: var(--app-text) !important;
}

/* Both selectors are needed: Vuetify's theme flips with .dark-mode, so the
   tick renders as .theme--dark.v-icon here, not .theme--light.v-icon. */
.dark-mode .er-wrap .er-checkbox.v-input--checkbox .theme--light.v-icon,
.dark-mode .er-wrap .er-checkbox.v-input--checkbox .v-icon {
  color: var(--app-text-faint) !important;
}

/* Checked state. The light rule for .v-input--is-label-active paints the
   pill #f0f7ff, and it out-specifies the plain dark override above — so a
   ticked "Active" ended up as near-white fill under the dark theme's light
   label text. Restate the whole checked look on the sky-tinted wash. */
.dark-mode .er-wrap .er-checkbox.v-input--checkbox.v-input--is-label-active {
  background: var(--app-accent-soft);
  border-color: var(--app-accent-border);
}

.dark-mode
  .er-wrap
  .er-checkbox.v-input--checkbox.v-input--is-label-active
  .v-label {
  color: var(--app-text) !important;
}

.dark-mode
  .er-wrap
  .er-checkbox.v-input--checkbox.v-input--is-label-active
  .theme--light.v-icon,
.dark-mode
  .er-wrap
  .er-checkbox.v-input--checkbox.v-input--is-label-active
  .v-icon {
  color: var(--app-accent-strong) !important;
}

.dark-mode .er-wrap .er-editor .tiptap-vuetify-editor .v-toolbar {
  border-bottom: 1px solid var(--app-border) !important;
  background: var(--app-surface) !important;
}

.dark-mode .er-wrap .er-editor .tiptap-vuetify-editor__toolbar .v-btn {
  color: var(--app-text-muted) !important;
}

.dark-mode .er-wrap .er-editor .ProseMirror {
  color: var(--app-text) !important;
}

.dark-mode .er-wrap .er-editor .ProseMirror p.is-editor-empty:first-child::before {
  color: var(--app-text-faint) !important;
}

/* >>> end auto-dark <<< */

/* Unscoped: Vuetify input + checkbox overrides - matches HRDC theme.
   Prefixed with .er-wrap so it stays effectively scoped to this page. */
.er-wrap .er-input.v-text-field--outlined > .v-input__control > .v-input__slot {
  border-radius: 10px !important;
  min-height: 42px;
  background: #ffffff !important;
}
.er-wrap .er-input.v-text-field--outlined fieldset {
  border-color: #cbd5e1 !important;
  background: transparent !important;
  transition: all 0.2s ease;
}
.er-wrap .er-input.v-text-field--outlined fieldset legend {
  width: 0 !important;
  padding: 0 !important;
  max-width: 0 !important;
  overflow: hidden !important;
}
.er-wrap
  .er-input.v-text-field--outlined
  > .v-input__control
  > .v-input__slot
  fieldset {
  top: 0 !important;
}
.er-wrap .er-input .v-label {
  display: none !important;
}
.er-wrap .er-input.v-text-field--outlined:hover fieldset {
  border-color: #94a3b8 !important;
}
.er-wrap .er-input.v-input--is-focused fieldset {
  border-color: #0ea5e9 !important;
  border-width: 2px !important;
}
.er-wrap .er-input.v-input--is-focused > .v-input__control > .v-input__slot {
  background: #fff !important;
}
.er-wrap .er-input .v-text-field__slot input {
  font-family: "Lato", sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  color: #1a1e24 !important;
}
.er-wrap .er-input .v-text-field__slot input::placeholder {
  color: #94a3b8 !important;
  font-weight: 500 !important;
}
/* Hide number-input spinners */
.er-wrap .er-input input[type="number"]::-webkit-outer-spin-button,
.er-wrap .er-input input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.er-wrap .er-input input[type="number"] {
  -moz-appearance: textfield;
}

/* Recipient chip ("banner") input - each email is its own tag */
.er-wrap .er-recipients .v-select__slot {
  min-height: 42px;
  padding: 5px 0;
}
.er-wrap .er-recipients .v-select__selections {
  padding: 2px 0;
}
.er-wrap .er-recipients .v-chip {
  background: #f0f7ff !important;
  border: 1px solid #c5ddf6 !important;
  border-radius: 7px !important;
  color: #1e293b !important;
  font-family: "Lato", sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 600 !important;
  height: 30px !important;
  padding: 0 12px !important;
  margin: 4px 6px 4px 0 !important;
}
.er-wrap .er-recipients .v-chip .v-chip__close {
  color: #94a3b8 !important;
  font-size: 16px !important;
  margin-left: 8px;
}
.er-wrap .er-recipients .v-chip .v-chip__close:hover {
  color: #ef4444 !important;
}
.er-wrap .er-recipients input {
  font-family: "Lato", sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  color: #1a1e24 !important;
}
.er-wrap .er-recipients input::placeholder {
  color: #94a3b8 !important;
  font-weight: 500 !important;
}

/* Checkbox - HRDC pill style */
.er-wrap .er-checkbox.v-input--checkbox {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 7px 12px !important;
  margin: 0 !important;
  transition: all 0.15s ease;
  display: inline-flex;
  width: auto;
}
.er-wrap .er-checkbox.v-input--checkbox:hover {
  border-color: #94a3b8;
}
.er-wrap .er-checkbox.v-input--checkbox .v-label {
  font-family: "Lato", sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 700 !important;
  color: #334155 !important;
}
.er-wrap .er-checkbox.v-input--checkbox.v-input--is-label-active {
  background: #f0f7ff;
  border-color: #0ea5e9;
}
.er-wrap .er-checkbox.v-input--checkbox .v-icon {
  font-size: 20px !important;
}
.er-wrap .er-checkbox.v-input--checkbox .theme--light.v-icon {
  color: #cbd5e1 !important;
}
.er-wrap
  .er-checkbox.v-input--checkbox.v-input--is-label-active
  .theme--light.v-icon {
  color: #0ea5e9 !important;
}

/* Tiptap WYSIWYG editor - HRDC theme */
.er-wrap .er-editor .tiptap-vuetify-editor .v-card {
  box-shadow: none !important;
  border: none !important;
  border-radius: 0 !important;
}
.er-wrap .er-editor .tiptap-vuetify-editor .v-toolbar {
  box-shadow: none !important;
  border-bottom: 1px solid #e2e8f0 !important;
  background: #f8fafc !important;
}
.er-wrap .er-editor .tiptap-vuetify-editor__toolbar .v-btn {
  color: #64748b !important;
}
.er-wrap .er-editor .ProseMirror {
  min-height: 220px;
  padding: 16px 18px !important;
  font-family: "Lato", sans-serif !important;
  font-size: 0.86rem !important;
  color: #334155 !important;
  line-height: 1.7 !important;
}
.er-wrap .er-editor .ProseMirror:focus {
  outline: none;
}
.er-wrap .er-editor .ProseMirror p.is-editor-empty:first-child::before {
  color: #94a3b8 !important;
}


/* >>> dark companions: the light rules above pin these to a dark ink
   with !important, which on the dark field renders the text invisible
   rather than absent. Token references only. <<< */

.dark-mode .er-wrap .er-input .v-text-field__slot input {
  color: var(--app-text) !important;
}

.dark-mode .er-wrap .er-recipients input {
  color: var(--app-text) !important;
}
</style>
